/* ============================================
   PREDICTIVE SEARCH - JavaScript
   Advanced Search with Caching & Keyboard Nav
   ============================================ */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    minChars: 2,
    debounceDelay: 300,
    maxProducts: 6,
    maxCollections: 3,
    maxSuggestions: 5,
    maxRecentSearches: 5,
    cacheExpiry: 5 * 60 * 1000, // 5 minutes
    storageKey: 'ps_recent_searches',
    popularStorageKey: 'ps_popular_searches'
  };

  // Cache for search results
  const searchCache = new Map();

  // State
  let currentQuery = '';
  let highlightedIndex = -1;
  let allItems = [];
  let isOpen = false;

  // DOM Elements
  let wrapper, input, results, overlay, loadingSpinner;

  // Initialize
  function init() {
    wrapper = document.querySelector('.predictive-search-wrapper');
    if (!wrapper) return;

    input = wrapper.querySelector('.predictive-search-input');
    results = wrapper.querySelector('.predictive-search-results');
    overlay = document.querySelector('.ps-overlay');
    loadingSpinner = wrapper.querySelector('.search-loading');

    if (!input || !results) return;

    bindEvents();
    loadInitialState();
  }

  // Bind Events
  function bindEvents() {
    // Input events
    input.addEventListener('input', debounce(handleInput, CONFIG.debounceDelay));
    input.addEventListener('focus', handleFocus);
    input.addEventListener('keydown', handleKeydown);

    // Click outside to close
    document.addEventListener('click', handleClickOutside);

    // Overlay click
    if (overlay) {
      overlay.addEventListener('click', closeResults);
    }

    // Form submit
    const form = wrapper.querySelector('form');
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }
  }

  // Load Initial State (Recent & Popular)
  function loadInitialState() {
    // Nothing to load initially, will show on focus
  }

  // Handle Input
  function handleInput(e) {
    const query = e.target.value.trim();
    currentQuery = query;

    if (query.length < CONFIG.minChars) {
      showInitialState();
      return;
    }

    showLoading(true);
    performSearch(query);
  }

  // Handle Focus
  function handleFocus() {
    if (currentQuery.length >= CONFIG.minChars) {
      performSearch(currentQuery);
    } else {
      showInitialState();
    }
  }

  // Handle Keydown (Keyboard Navigation)
  function handleKeydown(e) {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        navigateItems(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateItems(-1);
        break;
      case 'Enter':
        e.preventDefault();
        selectHighlighted();
        break;
      case 'Escape':
        closeResults();
        input.blur();
        break;
    }
  }

  // Navigate Items
  function navigateItems(direction) {
    allItems = results.querySelectorAll('[data-navigate]');
    if (allItems.length === 0) return;

    // Remove current highlight
    if (highlightedIndex >= 0 && allItems[highlightedIndex]) {
      allItems[highlightedIndex].classList.remove('highlighted');
    }

    // Calculate new index
    highlightedIndex += direction;
    if (highlightedIndex < 0) highlightedIndex = allItems.length - 1;
    if (highlightedIndex >= allItems.length) highlightedIndex = 0;

    // Add highlight
    allItems[highlightedIndex].classList.add('highlighted');
    allItems[highlightedIndex].scrollIntoView({ block: 'nearest' });
  }

  // Select Highlighted Item
  function selectHighlighted() {
    if (highlightedIndex >= 0 && allItems[highlightedIndex]) {
      const item = allItems[highlightedIndex];
      
      if (item.dataset.searchTerm) {
        // It's a search term suggestion
        input.value = item.dataset.searchTerm;
        saveRecentSearch(item.dataset.searchTerm);
        performSearch(item.dataset.searchTerm);
      } else if (item.href) {
        // It's a link (product or collection)
        saveRecentSearch(currentQuery);
        window.location.href = item.href;
      }
    } else {
      // Submit the form
      const form = wrapper.querySelector('form');
      if (form) {
        saveRecentSearch(currentQuery);
        form.submit();
      }
    }
  }

  // Handle Click Outside
  function handleClickOutside(e) {
    if (!wrapper.contains(e.target)) {
      closeResults();
    }
  }

  // Handle Form Submit
  function handleSubmit() {
    if (currentQuery.length > 0) {
      saveRecentSearch(currentQuery);
      updatePopularSearches(currentQuery);
    }
  }

  // Perform Search
  async function performSearch(query) {
    // Check cache first
    const cached = getFromCache(query);
    if (cached) {
      renderResults(cached, query);
      showLoading(false);
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=10&resources[options][unavailable_products]=last`);
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      // Cache the results
      setToCache(query, data);
      
      renderResults(data, query);
    } catch (error) {
      console.error('Predictive search error:', error);
      showNoResults(query);
    } finally {
      showLoading(false);
    }
  }

  // Render Results
  function renderResults(data, query) {
    const resources = data.resources?.results || {};
    const products = resources.products || [];
    const collections = resources.collections || [];
    const queries = resources.queries || [];

    let html = '';
    highlightedIndex = -1;

    // Suggestions
    if (queries.length > 0) {
      html += renderSuggestions(queries, query);
    }

    // Products
    if (products.length > 0) {
      html += renderProducts(products.slice(0, CONFIG.maxProducts), query);
    }

    // Collections
    if (collections.length > 0) {
      html += renderCollections(collections.slice(0, CONFIG.maxCollections));
    }

    // No results
    if (products.length === 0 && collections.length === 0) {
      html += renderNoResults(query);
    } else {
      // View all results button
      html += renderViewAllButton(query, products.length);
    }

    results.innerHTML = html;
    openResults();
    bindResultsEvents();
  }

  // Render Suggestions
  function renderSuggestions(queries, query) {
    const items = queries.slice(0, CONFIG.maxSuggestions).map(q => {
      const highlighted = highlightMatch(q.text, query);
      return `
        <li class="ps-suggestion-item" data-navigate data-search-term="${escapeHtml(q.text)}">
          <i class="fa fa-search"></i>
          <span>${highlighted}</span>
        </li>
      `;
    }).join('');

    return `
      <div class="ps-section ps-suggestions">
        <div class="ps-section-header">
          <span class="ps-section-title">
            <i class="fa fa-lightbulb-o"></i>
            Vorschläge
          </span>
        </div>
        <ul class="ps-suggestions-list">
          ${items}
        </ul>
      </div>
    `;
  }

  // Render Products
  function renderProducts(products, query) {
    const items = products.map(product => {
      const image = product.image || product.featured_image?.url || '';
      const price = formatMoney(product.price);
      const comparePrice = product.compare_at_price_max > product.price ? formatMoney(product.compare_at_price_max) : '';
      const discount = comparePrice ? calculateDiscount(product.price, product.compare_at_price_max) : 0;
      
      // Rating (if available via metafields or reviews app)
      const rating = product.metafields?.reviews?.rating?.value || null;
      const reviewCount = product.metafields?.reviews?.rating_count?.value || 0;

      return `
        <a href="${product.url}" class="ps-product-item" data-navigate>
          <div class="ps-product-image">
            ${image ? `<img src="${getSizedImage(image, '100x100')}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>
          <div class="ps-product-info">
            <div class="ps-product-title">${escapeHtml(product.title)}</div>
            ${rating ? renderRating(rating, reviewCount) : ''}
            <div class="ps-product-price">
              <span class="ps-price-current">${price}</span>
              ${comparePrice ? `<span class="ps-price-original">${comparePrice}</span>` : ''}
              ${discount > 0 ? `<span class="ps-price-discount">-${discount}%</span>` : ''}
            </div>
          </div>
        </a>
      `;
    }).join('');

    return `
      <div class="ps-section ps-products">
        <div class="ps-products-header">
          <span class="ps-section-title">
            <i class="fa fa-cube"></i>
            Produkte
          </span>
          <a href="/search?q=${encodeURIComponent(query)}&type=product" class="ps-view-all">
            Alle anzeigen <i class="fa fa-chevron-right"></i>
          </a>
        </div>
        <div class="ps-products-list">
          ${items}
        </div>
      </div>
    `;
  }

  // Render Rating
  function renderRating(rating, count) {
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    let stars = '';
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars += '<i class="fa fa-star"></i>';
      } else if (i === fullStars && hasHalf) {
        stars += '<i class="fa fa-star-half-o"></i>';
      } else {
        stars += '<i class="fa fa-star-o"></i>';
      }
    }

    return `
      <div class="ps-product-rating">
        <span class="ps-product-stars">${stars}</span>
        ${count > 0 ? `<span class="ps-product-reviews">(${count})</span>` : ''}
      </div>
    `;
  }

  // Render Collections
  function renderCollections(collections) {
    const icons = ['fa-folder', 'fa-tags', 'fa-th-large'];
    
    const items = collections.map((collection, index) => {
      return `
        <a href="${collection.url}" class="ps-collection-item" data-navigate>
          <i class="fa ${icons[index % icons.length]} ps-collection-icon"></i>
          <span class="ps-collection-name">${escapeHtml(collection.title)}</span>
          ${collection.products_count ? `<span class="ps-collection-count">${collection.products_count} Produkte</span>` : ''}
        </a>
      `;
    }).join('');

    return `
      <div class="ps-section ps-collections">
        <div class="ps-section-header">
          <span class="ps-section-title">
            <i class="fa fa-folder-open"></i>
            Kollektionen
          </span>
        </div>
        <div class="ps-collections-grid">
          ${items}
        </div>
      </div>
    `;
  }

  // Render No Results
  function renderNoResults(query) {
    return `
      <div class="ps-no-results">
        <i class="fa fa-search"></i>
        <p>Keine Ergebnisse für "${escapeHtml(query)}"</p>
        <span>Versuchen Sie es mit anderen Suchbegriffen</span>
      </div>
    `;
  }

  // Render View All Button
  function renderViewAllButton(query, count) {
    return `
      <a href="/search?q=${encodeURIComponent(query)}&type=product" class="ps-view-all-results" data-navigate>
        <i class="fa fa-search"></i>
        <span>Alle Ergebnisse für "${escapeHtml(query)}" anzeigen</span>
      </a>
    `;
  }

  // Show Initial State (Recent + Popular)
  function showInitialState() {
    const recentSearches = getRecentSearches();
    const popularSearches = getPopularSearches();

    let html = '';

    // Recent Searches
    if (recentSearches.length > 0) {
      html += renderRecentSearches(recentSearches);
    }

    // Popular Searches
    if (popularSearches.length > 0) {
      html += renderPopularSearches(popularSearches);
    }

    if (html) {
      results.innerHTML = html;
      openResults();
      bindResultsEvents();
    } else {
      closeResults();
    }
  }

  // Render Recent Searches
  function renderRecentSearches(searches) {
    const items = searches.map(search => `
      <li class="ps-recent-item" data-navigate data-search-term="${escapeHtml(search)}">
        <i class="fa fa-history"></i>
        <span>${escapeHtml(search)}</span>
      </li>
    `).join('');

    return `
      <div class="ps-section ps-recent">
        <div class="ps-section-header">
          <span class="ps-section-title">
            <i class="fa fa-clock-o"></i>
            Letzte Suchen
          </span>
          <button class="ps-clear-btn" data-action="clear-recent">
            <i class="fa fa-times"></i> Verlauf löschen
          </button>
        </div>
        <ul class="ps-recent-list">
          ${items}
        </ul>
      </div>
    `;
  }

  // Render Popular Searches
  function renderPopularSearches(searches) {
    const items = searches.map(search => `
      <span class="ps-tag" data-navigate data-search-term="${escapeHtml(search)}">
        <i class="fa fa-fire"></i>
        ${escapeHtml(search)}
      </span>
    `).join('');

    return `
      <div class="ps-section ps-popular">
        <div class="ps-section-header">
          <span class="ps-section-title">
            <i class="fa fa-fire"></i>
            Beliebte Suchen
          </span>
        </div>
        <div class="ps-tags">
          ${items}
        </div>
      </div>
    `;
  }

  // Bind Results Events
  function bindResultsEvents() {
    // Click on search term items
    results.querySelectorAll('[data-search-term]').forEach(item => {
      item.addEventListener('click', function(e) {
        e.preventDefault();
        const term = this.dataset.searchTerm;
        input.value = term;
        currentQuery = term;
        saveRecentSearch(term);
        performSearch(term);
      });
    });

    // Clear recent searches
    const clearBtn = results.querySelector('[data-action="clear-recent"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        clearRecentSearches();
        showInitialState();
      });
    }

    // Product/Collection links
    results.querySelectorAll('a[href]').forEach(link => {
      link.addEventListener('click', function() {
        saveRecentSearch(currentQuery);
      });
    });
  }

  // Open Results
  function openResults() {
    results.classList.add('active');
    if (overlay) overlay.classList.add('active');
    isOpen = true;
  }

  // Close Results
  function closeResults() {
    results.classList.remove('active');
    if (overlay) overlay.classList.remove('active');
    isOpen = false;
    highlightedIndex = -1;
  }

  // Show Loading
  function showLoading(show) {
    if (loadingSpinner) {
      loadingSpinner.classList.toggle('active', show);
    }
  }

  // Show No Results
  function showNoResults(query) {
    results.innerHTML = renderNoResults(query);
    openResults();
  }

  // ============ Storage Functions ============

  // Get Recent Searches
  function getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(CONFIG.storageKey)) || [];
    } catch {
      return [];
    }
  }

  // Save Recent Search
  function saveRecentSearch(query) {
    if (!query || query.length < CONFIG.minChars) return;
    
    let searches = getRecentSearches();
    searches = searches.filter(s => s.toLowerCase() !== query.toLowerCase());
    searches.unshift(query);
    searches = searches.slice(0, CONFIG.maxRecentSearches);
    
    try {
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(searches));
    } catch (e) {
      console.warn('Could not save recent search:', e);
    }
  }

  // Clear Recent Searches
  function clearRecentSearches() {
    try {
      localStorage.removeItem(CONFIG.storageKey);
    } catch (e) {
      console.warn('Could not clear recent searches:', e);
    }
  }

  // Get Popular Searches (Dynamic)
  function getPopularSearches() {
    try {
      const popular = JSON.parse(localStorage.getItem(CONFIG.popularStorageKey)) || {};
      // Sort by count and return top items
      return Object.entries(popular)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([term]) => term);
    } catch {
      // Fallback to default popular searches
      return window.defaultPopularSearches || ['Weihnachtsbaum', 'Kerzen', 'Kugeln', 'Lichterkette', 'Geschenk', 'Deko'];
    }
  }

  // Update Popular Searches
  function updatePopularSearches(query) {
    if (!query || query.length < CONFIG.minChars) return;
    
    try {
      let popular = JSON.parse(localStorage.getItem(CONFIG.popularStorageKey)) || {};
      const normalizedQuery = query.toLowerCase().trim();
      popular[normalizedQuery] = (popular[normalizedQuery] || 0) + 1;
      localStorage.setItem(CONFIG.popularStorageKey, JSON.stringify(popular));
    } catch (e) {
      console.warn('Could not update popular searches:', e);
    }
  }

  // ============ Cache Functions ============

  // Get from Cache
  function getFromCache(query) {
    const cached = searchCache.get(query.toLowerCase());
    if (cached && Date.now() - cached.timestamp < CONFIG.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  // Set to Cache
  function setToCache(query, data) {
    searchCache.set(query.toLowerCase(), {
      data: data,
      timestamp: Date.now()
    });
  }

  // ============ Utility Functions ============

  // Debounce
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Format Money
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents);
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + amount;
  }

  // Calculate Discount
  function calculateDiscount(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return 0;
    return Math.round((1 - price / comparePrice) * 100);
  }

  // Get Sized Image
  function getSizedImage(url, size) {
    if (!url) return '';
    return url.replace(/\.([^.]+)$/, `_${size}.$1`);
  }

  // Escape HTML
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Highlight Match
  function highlightMatch(text, query) {
    if (!query) return escapeHtml(text);
    const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
    return escapeHtml(text).replace(regex, '<mark>$1</mark>');
  }

  // Escape Regex
  function escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Initialize on DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
